/**
 * CSV Parser utility for CRM Lead import/export
 * Handles parsing CSV files and converting to/from lead objects
 */

export interface ParsedCSVRow {
  [key: string]: string | null;
}

export interface ParseError {
  row: number;
  field: string;
  message: string;
}

/**
 * Parse CSV content into rows with headers
 * Handles quoted fields and special characters
 */
export const parseCSV = (csvContent: string): { headers: string[]; rows: ParsedCSVRow[]; errors: ParseError[] } => {
  const errors: ParseError[] = [];
  const lines = csvContent.split('\n').filter(line => line.trim());
  
  if (lines.length === 0) {
    return { headers: [], rows: [], errors: [{ row: 0, field: '', message: 'Empty CSV file' }] };
  }

  // Remove BOM if present
  const firstLine = lines[0].startsWith('\uFEFF') ? lines[0].slice(1) : lines[0];
  
  const headers = parseCSVLine(firstLine);
  const rows: ParsedCSVRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    
    if (values.length === 0) continue; // Skip empty lines
    
    const row: ParsedCSVRow = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || null;
    });
    
    rows.push(row);
  }

  return { headers, rows, errors };
};

/**
 * Parse a single CSV line, handling quoted fields
 */
const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === ',' && !insideQuotes) {
      // Field separator
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

/**
 * Validate a parsed lead row for required fields
 */
export const validateLeadRow = (row: ParsedCSVRow, headers: string[]): ParseError[] => {
  const errors: ParseError[] = [];
  const requiredFields = ['trade_name', 'contact_name', 'contact_phone', 'status', 'role'];
  
  requiredFields.forEach(field => {
    if (!row[field] || !row[field]?.trim()) {
      errors.push({
        row: 0,
        field,
        message: `Required field "${field}" is missing or empty`
      });
    }
  });

  // Validate email format if present
  if (row['contact_email'] && row['contact_email']?.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(row['contact_email'])) {
      errors.push({
        row: 0,
        field: 'contact_email',
        message: 'Invalid email format'
      });
    }
  }

  // Validate phone format
  if (row['contact_phone'] && !/^\d{7,15}$/.test(row['contact_phone'].replace(/[\s\-\(\)]/g, ''))) {
    errors.push({
      row: 0,
      field: 'contact_phone',
      message: 'Phone number should contain 7-15 digits'
    });
  }

  // Validate additional_contacts format if present
  if (row['additional_contacts'] && row['additional_contacts']?.trim()) {
    const contacts = row['additional_contacts'].split('|');
    for (let i = 0; i < contacts.length; i++) {
      const parts = contacts[i].split(':').map(p => p.trim());
      if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) {
        errors.push({
          row: 0,
          field: 'additional_contacts',
          message: `Entry ${i + 1}: Format should be "Name:Phone:Type:PhoneType:Role:Email:Source" (at least first 3 are required)`
        });
        break;
      }
      const contactType = parts[2]?.toLowerCase();
      if (!['secondary', 'other'].includes(contactType)) {
        errors.push({
          row: 0,
          field: 'additional_contacts',
          message: `Entry ${i + 1}: Type should be "secondary" or "other"`
        });
        break;
      }
      if (parts[3]) {
        const phoneType = parts[3]?.toLowerCase();
        if (phoneType && !['mobile', 'landline'].includes(phoneType)) {
          errors.push({
            row: 0,
            field: 'additional_contacts',
            message: `Entry ${i + 1}: Phone Type should be "mobile" or "landline"`
          });
          break;
        }
      }
      // Validate phone in additional contacts
      if (!/^\d{7,15}$/.test(parts[1].replace(/[\s\-\(\)]/g, ''))) {
        errors.push({
          row: 0,
          field: 'additional_contacts',
          message: `Entry ${i + 1}: Phone number should contain 7-15 digits`
        });
        break;
      }
    }
  }

  // Validate additional_addresses format if present
  if (row['additional_addresses'] && row['additional_addresses']?.trim()) {
    const addresses = row['additional_addresses'].split('|');
    for (let i = 0; i < addresses.length; i++) {
      const parts = addresses[i].split(':').map(p => p.trim());
      if (parts.length < 4) {
        errors.push({
          row: 0,
          field: 'additional_addresses',
          message: `Entry ${i + 1}: Format should be "Type:Line1:Line2:Line3:Pincode:Landmark:Category:Lat:Lng" (at least 4 required)`
        });
        break;
      }
      if (!parts[1] || !parts[3]) {
        errors.push({
          row: 0,
          field: 'additional_addresses',
          message: `Entry ${i + 1}: Address line 1 and pincode are required`
        });
        break;
      }
    }
  }

  // Validate notes format if present
  if (row['notes'] && row['notes']?.trim()) {
    const notes = row['notes'].split('|');
    for (let i = 0; i < notes.length; i++) {
      const parts = notes[i].split(':').map(p => p.trim());
      if (parts.length < 2 || !parts[0] || !parts[1]) {
        errors.push({
          row: 0,
          field: 'notes',
          message: `Entry ${i + 1}: Format should be "Author:Text:Category:Sentiment:Priority:Status:NextMeeting:Time:ContactName"`
        });
        break;
      }
    }
  }

  return errors;
};

/**
 * Parse multiple phone numbers from pipe-delimited format
 * Format: "name:phone:type:phoneType:role:email:source|..."
 */
const parseAdditionalPhones = (value: string): Array<{ 
  name: string; 
  phone: string; 
  type: string; 
  phone_type: string;
  role?: string;
  email?: string;
  source?: string;
}> => {
  if (!value || !value.trim()) return [];
  
  return value.split('|').map(entry => {
    const parts = entry.split(':').map(p => p.trim());
    return {
      name: parts[0] || '',
      phone: parts[1] || '',
      type: parts[2]?.toLowerCase() === 'other' ? 'other' : 'secondary',
      phone_type: parts[3]?.toLowerCase() === 'landline' ? 'landline' : 'mobile',
      role: parts[4] || '',
      email: parts[5] || null,
      source: parts[6] || null,
    };
  }).filter(e => e.name && e.phone);
};

/**
 * Parse multiple addresses from pipe-delimited format
 * Format: "type:line1:line2:line3:pincode:landmark:category:lat:lng|..."
 */
const parseAdditionalAddresses = (value: string): Array<{ 
  type: string; 
  line1: string; 
  line2?: string; 
  line3: string; 
  pincode: string; 
  landmark?: string;
  category?: string;
  lat?: string;
  lng?: string;
}> => {
  if (!value || !value.trim()) return [];
  
  return value.split('|').map(entry => {
    const parts = entry.split(':').map(p => p.trim());
    return {
      type: parts[0] || 'secondary',
      line1: parts[1] || '',
      line2: parts[2] || undefined,
      line3: parts[3] || '',
      pincode: parts[4] || '',
      landmark: parts[5] || undefined,
      category: parts[6] || undefined,
      lat: parts[7] || undefined,
      lng: parts[8] || undefined,
    };
  }).filter(e => e.line1 && e.line3);
};

/**
 * Parse multiple notes from pipe-delimited format
 * Format: "author:text:category:sentiment:priority:status:nextMeeting:time:contactName|..."
 */
const parseNotes = (value: string): Array<any> => {
  if (!value || !value.trim()) return [];
  
  return value.split('|').map(entry => {
    const parts = entry.split(':').map(p => p.trim());
    return {
      author: parts[0] || 'System',
      text: parts[1] || '',
      category: parts[2]?.toLowerCase() || 'other',
      sentiment: parts[3]?.toLowerCase() || 'neutral',
      priority: parts[4]?.toLowerCase() || 'medium',
      status: parts[5]?.toLowerCase() || 'completed',
      next_meeting: parts[6] || null,
      next_meeting_time: parts[7] || null,
      contact_name: parts[8] || null,
    };
  }).filter(n => n.text);
};

/**
 * Convert parsed CSV row to lead object for API submission
 */
export const csvRowToLead = (row: ParsedCSVRow): Record<string, any> => {
  const lead: Record<string, any> = {
    trade_name: row['trade_name']?.trim() || '',
    registered_name: row['registered_name']?.trim() || null,
    status: row['status']?.trim() || '',
    follow_up_date: row['follow_up_date']?.trim() || null,
    role: row['role']?.trim() || '',
    contact_name: row['contact_name']?.trim() || '',
    contact_type: row['contact_type']?.trim() || 'primary',
    contact_phone_type: row['contact_phone_type']?.trim()?.toLowerCase() || 'mobile',
    contact_phone: row['contact_phone']?.trim() || '',
    contact_email: row['contact_email']?.trim() || null,
    lead_source_name: row['lead_source_name']?.trim() || null,
    contact_other_lead_source: row['contact_other_lead_source']?.trim() || null,
    
    // Primary Address
    primary_address_line1: row['primary_address_line1']?.trim() || '',
    primary_address_line2: row['primary_address_line2']?.trim() || null,
    primary_address_line3: row['primary_address_line3']?.trim() || '',
    primary_address_landmark: row['primary_address_landmark']?.trim() || null,
    primary_address_pincode: row['primary_address_pincode']?.trim() || '',
    primary_address_lat: row['primary_address_lat']?.trim() || null,
    primary_address_lng: row['primary_address_lng']?.trim() || null,
    
    address_category: row['address_category']?.trim() || null,
    address_type: row['address_type']?.trim() || 'primary',
    
    assigned_to: row['assigned_to']?.trim() || null,
    lead_type: row['lead_type']?.trim() || '',
    program_name: row['program_name']?.trim() || null,
    website: row['website']?.trim() ? (row['website'].trim().startsWith('http') ? row['website'].trim() : `https://${row['website'].trim()}`) : null,
    associated_sales_id: row['associated_sales_id']?.trim() || null,
    bap_name: row['bap_name']?.trim() || null,
    
    additional_contacts: parseAdditionalPhones(row['additional_contacts'] || ''),
    additional_addresses: parseAdditionalAddresses(row['additional_addresses'] || ''),
    notes: parseNotes(row['notes'] || ''),
  };
  
  return lead;
};
